import React, { useMemo, useRef } from 'react';
import parse, { domToReact } from 'html-react-parser';
import { toLower } from 'lodash';
import { ElementsRenderer } from '../shared';
import FIBBlankReview from './FIBBlankReview';
import { REVIEW_STATUS } from '../../constants';
import { renderKatex } from '../../utils/domUtils';
import { StatusBadge } from '../ui/status-badge';

const reviewVariantMap = {
  CORRECT: 'correct',
  PARTIAL: 'partial',
  SKIPPED: 'skipped',
  INCORRECT: 'incorrect'
};

const extractBlankIds = (html) => {
  if (!html) return [];
  const ids = [];
  const regex = /blankid=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    ids.push(match[1]);
  }
  return ids;
};

const FIBReview = ({ question, response, answerStatus, answerScoreData }) => {
  const { elements, score, solution } = question;
  const { answers } = response;

  const fibText = elements?.[0]?.html;
  const blankConfig = question?.fibMetadata?.blankConfig;
  const blankIds = useMemo(() => extractBlankIds(fibText), [fibText]);

  const isPartial = useRef(false);

  const serverBlanks = answerScoreData?.blanks;

  const answersReview = useMemo(() => {
    const reviews = Object.entries(answers || {}).reduce((review, [key, value]) => {
      // Prefer the per-blank verdict persisted at grading time (accounts for whitespace/case
      // leniency and AI-accepted equivalents like "CO2" vs "carbon dioxide"). Fall back to an
      // exact match only for older submissions graded before per-blank verdicts were stored.
      const serverBlank = serverBlanks?.[key];
      const isCorrect = serverBlank
        ? serverBlank.isCorrect === true
        : toLower(blankConfig?.[key]?.answers?.[0]) === toLower(value?.[0]);
      // If one of the answers is right, but not all of them are
      if (isCorrect && answerStatus === REVIEW_STATUS.INCORRECT) {
        isPartial.current = true;
      }
      review[key] = {
        answers: blankConfig?.[key]?.answers,
        value,
        isCorrect
      };
      return review;
    }, {});

    return { reviews };
  }, [answers, serverBlanks, blankConfig, answerStatus]);

  const parseReplaceOptions = useMemo(
    () => ({
      replace: (domNode) => {
        if (!domNode) return;

        switch (domNode.name) {
          case 'p':
            return (
              <p className="leading-relaxed text-foreground">
                {domToReact(domNode?.children, parseReplaceOptions)}
              </p>
            );
          case 'ol':
            return (
              <ol className="my-2 list-decimal pl-5">
                {domToReact(domNode?.children, parseReplaceOptions)}
              </ol>
            );
          case 'li':
            return (
              <li className="my-2 leading-relaxed">
                {domToReact(domNode?.children, parseReplaceOptions)}
              </li>
            );
          case 'fill-in-blanks': {
            const blankId = domNode.attribs.blankid;
            return <FIBBlankReview {...answersReview?.reviews[blankId]} />;
          }
          default:
            break;
        }

        const katexRendered = renderKatex(domNode);
        if (katexRendered !== undefined) {
          return katexRendered;
        }
      }
    }),
    [answersReview]
  );

  const parsedHtmlContent = useMemo(() => {
    return parse(fibText, parseReplaceOptions);
  }, [fibText, parseReplaceOptions]);

  const displayStatus =
    isPartial.current && answerStatus === REVIEW_STATUS.INCORRECT
      ? REVIEW_STATUS.PARTIAL
      : answerStatus;

  return (
    <div className="katex-question w-full space-y-4 md:space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge variant={reviewVariantMap[displayStatus] ?? 'default'}>
          Score {answerScoreData?.score ?? 0} / {score}
        </StatusBadge>
      </div>

      <div className="space-y-4">
        <div className="text-base leading-relaxed text-foreground md:text-lg">{parsedHtmlContent}</div>

        {blankIds.length > 0 && (
          <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-4">
            <h4 className="font-medium text-foreground">Your answers</h4>
            {blankIds.map((blankId, index) => {
              const review = answersReview.reviews[blankId];
              return (
                <div key={blankId} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="min-w-[60px] font-medium text-muted-foreground">
                    Blank {index + 1}:
                  </span>
                  <FIBBlankReview {...review} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {solution?.elements?.length > 0 && (
        <div className="space-y-3 border-t border-border/60 pt-4">
          <h4 className="font-medium text-foreground">Detailed Solution</h4>
          <div className="text-sm leading-relaxed text-muted-foreground">
            <ElementsRenderer elements={solution.elements} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FIBReview;
